import './BasicLayout.less'

import PropTypes from 'ant-design-vue/es/_util/vue-types'
import * as Vue from 'vue'
import { RouterView } from 'vue-router'

import 'ant-design-vue/es/layout/style'
import Layout from 'ant-design-vue/es/layout'

import { ContainerQuery } from 'vue-container-query'
import { SiderMenuWrapper } from './components'
import { getComponentFromProp, isFun } from './utils/util'
import { SiderMenuProps } from './components/SiderMenu'
import HeaderView, { HeaderViewProps } from './Header'
import WrapContent from './WrapContent'
import ConfigProvider from './components/ConfigProvider'
import PageHeaderWrapper from './components/PageHeaderWrapper'
import ContainerQueryWrapper from './ContainerQueryWrapper.vue'

export const BasicLayoutProps = {
  ...SiderMenuProps,
  ...HeaderViewProps,
  contentWidth: PropTypes.oneOf(['Fluid', 'Fixed']).def('Fluid'),
  // contentWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).def('Fluid'),
  locale: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).def('en-US'),
  breadcrumbRender: PropTypes.func,
  disableMobile: PropTypes.bool.def(false),
  mediaQuery: PropTypes.object.def({}),
  handleMediaQuery: PropTypes.func,
  footerRender: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]).def(undefined)
}

const MediaQueryEnum = {
  'screen-xs': {
    maxWidth: 575
  },
  'screen-sm': {
    minWidth: 576,
    maxWidth: 767
  },
  'screen-md': {
    minWidth: 768,
    maxWidth: 991
  },
  'screen-lg': {
    minWidth: 992,
    maxWidth: 1199
  },
  'screen-xl': {
    minWidth: 1200,
    maxWidth: 1599
  },
  'screen-xxl': {
    minWidth: 1600
  }
}

const getPaddingLeft = (
  hasLeftPadding,
  collapsed = undefined,
  siderWidth
) => {
  if (hasLeftPadding) {
    return collapsed ? 80 : siderWidth
  }
  return 0
}

const headerRender = (h, props) => {
  if (props.headerRender === false) {
    return null
  }
  return <HeaderView { ...{ props } } />
}

const defaultI18nRender = (key) => key

// 使用独立的 ContainerQueryWrapper.vue 组件
// 这样可以确保插槽正确传递，并且 ContainerQuery 只接收一个子元素

const BasicLayout = {
  name: 'BasicLayout',
  functional: true,
  props: BasicLayoutProps,
  // Vue 3 中函数式组件的 render 函数签名：render(props, context)
  // 第一个参数是 props，第二个参数是 context（包含 slots, attrs 等）
  render (props, context) {
    // 在 Vue 3 中，第一个参数就是 props
    // 调试：检查所有 props
    console.log('BasicLayout render: props (first param)', props)
    console.log('BasicLayout render: context (second param)', context)
    console.log('BasicLayout render: props.menus', props?.menus)
    
    // 在 Vue 3 中，插槽是一个函数，需要调用它来获取内容
    // 但是，如果插槽内容为空，我们需要使用 RouterView
    let children = []
    if (context?.slots?.default) {
      const slotResult = context.slots.default()
      children = Array.isArray(slotResult) ? slotResult : (slotResult ? [slotResult] : [])
    }
    
    // 调试：检查 children 内容
    console.log('BasicLayout: children from slots', children)
    console.log('BasicLayout: children length', Array.isArray(children) ? children.length : 'not array')
    if (Array.isArray(children) && children.length > 0) {
      console.log('BasicLayout: first child type', children[0]?.type?.name || children[0]?.type)
      // 检查第一个子元素是否是 RouterView
      if (children[0]?.type === RouterView || children[0]?.type?.name === 'RouterView') {
        console.log('BasicLayout: 第一个子元素是 RouterView，无需替换')
      }
    }
    
    const {
      layout = 'sidemenu',
      // theme,
      isMobile = false,
      collapsed = false,
      mediaQuery = {},
      handleMediaQuery,
      handleCollapse,
      siderWidth = 256,
      fixSiderbar = false,
      contentWidth = 'Fluid',
      i18nRender = defaultI18nRender,
      menus = props?.menus || [] // 从 props 中获取 menus，默认值为空数组
    } = props || {}

    // 在 Vue 3 中，使用 context 获取插槽和属性
    const footerRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'footerRender')
    const rightContentRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'rightContentRender')
    const collapsedButtonRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'collapsedButtonRender')
    const menuHeaderRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'menuHeaderRender')
    const breadcrumbRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'breadcrumbRender')
    const headerContentRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'headerContentRender')
    const menuRender = getComponentFromProp({ props, slots: context.slots, attrs: context.attrs }, 'menuRender')

    const isTopMenu = layout === 'topmenu'
    const hasSiderMenu = !isTopMenu
    // If it is a fix menu, calculate padding
    // don't need padding in phone mode
    const hasLeftPadding = fixSiderbar && !isTopMenu && !isMobile
    
    // 确保 menus 被包含在 props 中（已经从解构中获取）
    console.log('BasicLayout: menus from destructuring', menus)
    console.log('BasicLayout: menus length', menus.length)
    console.log('BasicLayout: menus is array?', Array.isArray(menus))
    
    // 在 Vue 3 中，不应该枚举组件实例的属性
    // 直接构建 cdProps，只包含需要的属性，避免使用解构的剩余参数（...restProps）
    // 这会触发 Vue 3 的枚举警告
    const cdProps = {
      layout,
      contentWidth,
      hasSiderMenu,
      menus, // 确保 menus 被传递
      footerRender,
      menuHeaderRender,
      rightContentRender,
      collapsedButtonRender,
      breadcrumbRender,
      headerContentRender,
      menuRender,
      isMobile,
      collapsed,
      mediaQuery,
      handleMediaQuery,
      handleCollapse,
      siderWidth,
      fixSiderbar,
      i18nRender
    }
    
    // 如果需要传递其他 props，直接访问 props 对象的属性，而不是使用解构
    // 这样可以避免触发枚举警告
    if (props) {
      // 只传递已知的、非 $ 开头的属性
      const knownProps = [
        'theme', 'primaryColor', 'multiTab', 'fixedHeader', 'colorWeak',
        'tableSize', 'tableBordered', 'hideFooter', 'hideHintAlert', 'hideCopyButton'
      ]
      knownProps.forEach(key => {
        if (key in props && !key.startsWith('$')) {
          cdProps[key] = props[key]
        }
      })
    }

    // 使用 Vue.h 创建所有内容，确保 ContainerQuery 只有一个子元素
    // 创建内部 Layout 的子元素数组
    const innerLayoutChildren = []
    
    // 添加 header（在 Vue 3 中，使用 Vue.h 而不是 h）
    const header = headerRender(Vue.h, {
      ...cdProps,
      mode: 'horizontal'
    })
    if (header) {
      innerLayoutChildren.push(header)
    }
    
    // 添加 WrapContent
    // 如果 children 为空（没有从插槽获取到内容），直接使用 RouterView
    // 这样可以确保 router-view 能够正确渲染
    const contentChildren = Array.isArray(children) && children.length > 0 
      ? children 
      : [Vue.h(RouterView)]
    
    console.log('BasicLayout: contentChildren', contentChildren)
    console.log('BasicLayout: contentChildren length', contentChildren.length)
    if (contentChildren.length > 0) {
      console.log('BasicLayout: contentChildren[0] type:', contentChildren[0]?.type?.name || contentChildren[0]?.type)
      console.log('BasicLayout: contentChildren[0] is RouterView?', 
        contentChildren[0]?.type === RouterView || 
        contentChildren[0]?.type?.name === 'RouterView' ||
        contentChildren[0]?.type?.__name === 'RouterView'
      )
    }
    
    innerLayoutChildren.push(
      Vue.h(WrapContent, {
        class: 'ant-pro-basicLayout-content',
        contentWidth: contentWidth
      }, {
        default: () => contentChildren
      })
    )
    
    // 如果有 footer，添加到内部 Layout
    if (footerRender !== false) {
      const footerContent = isFun(footerRender) ? footerRender(h) : footerRender
      if (footerContent) {
        innerLayoutChildren.push(
          Vue.h(Layout.Footer, null, {
            default: () => footerContent
          })
        )
      }
    }

    // 创建内部 Layout
    // 在 Vue 3 中，如果传递数组作为第三个参数，会被视为 children
    // 但如果组件期望插槽，应该使用对象形式传递插槽
    const innerLayout = Vue.h(Layout, {
      class: [layout],
      style: {
        paddingLeft: hasSiderMenu
          ? `${getPaddingLeft(!!hasLeftPadding, collapsed, siderWidth)}px`
          : undefined,
        minHeight: '100vh'
      }
    }, {
      default: () => innerLayoutChildren
    })

    // 创建外层 Layout（ContainerQuery 的唯一子元素）
    // 确保这是一个单个 VNode，不是数组
    // 调试：检查 menus 和 props
    console.log('BasicLayout: props.menus', props.menus)
    console.log('BasicLayout: cdProps', cdProps)
    console.log('BasicLayout: hasSiderMenu', hasSiderMenu, 'isTopMenu', isTopMenu)
    
    const outerLayoutChildren = []
    
    // 只有在非 topmenu 模式下才添加 SiderMenuWrapper
    if (hasSiderMenu) {
      // 直接使用 cdProps，它已经过滤掉了不应该传递的属性
      // 不需要再次使用 Object.keys 枚举，避免 Vue 3 警告
      const siderMenuProps = {
        ...cdProps,
        collapsed: collapsed,
        onCollapse: handleCollapse
      }
      
      outerLayoutChildren.push(
        Vue.h(SiderMenuWrapper, siderMenuProps)
      )
    }
    
    outerLayoutChildren.push(innerLayout)
    
    // 在 Vue 3 中，使用对象形式传递插槽，确保插槽是函数形式
    const outerLayout = Vue.h(Layout, {
      class: {
        'ant-pro-basicLayout': true,
        'ant-pro-topmenu': isTopMenu,
        ...mediaQuery
      }
    }, {
      default: () => outerLayoutChildren
    })

    // 关键修复：ContainerQuery 必须只有一个直接子元素
    // 最终解决方案：使用非函数式组件 ContainerQueryWrapper 来包装 ContainerQuery
    // 这样可以确保插槽正确传递，并且 ContainerQuery 只接收一个子元素
    
    // 调试：检查 outerLayout 和相关信息
    console.log('BasicLayout: outerLayout', outerLayout)
    console.log('BasicLayout: outerLayout type', typeof outerLayout, outerLayout?.type)
    console.log('BasicLayout: outerLayoutChildren length', outerLayoutChildren.length)
    console.log('BasicLayout: props.menus', props.menus)
    console.log('BasicLayout: layout', layout, 'isTopMenu', isTopMenu, 'hasSiderMenu', hasSiderMenu)
    
    // 临时测试：直接返回 outerLayout，不使用 ContainerQueryWrapper
    // 如果这样可以显示菜单，说明问题在 ContainerQueryWrapper
    console.log('BasicLayout: Returning outerLayout directly (bypassing ContainerQueryWrapper for testing)')
    return outerLayout
    
    // 如果直接返回可以显示菜单，再启用 ContainerQueryWrapper
    // return Vue.h(ContainerQueryWrapper, {
    //   query: MediaQueryEnum,
    //   onChange: handleMediaQuery || (() => {}),
    //   outerLayout: outerLayout,
    //   i18nRender: i18nRender,
    //   contentWidth: contentWidth,
    //   breadcrumbRender: breadcrumbRender
    // })
  }
}

BasicLayout.install = function (Vue) {
  Vue.component(PageHeaderWrapper.name, PageHeaderWrapper)
  Vue.component('PageContainer', PageHeaderWrapper)
  Vue.component('ProLayout', BasicLayout)
}

export default BasicLayout
